import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import Logo from '../components/Logo';
import GenerateFormControl from '../components/GenerateFormControl';
import SectionHeader from '../components/SectionHeader';
import { ReferralInfoSection } from '../components/ReferralDetailsSection';
import EmailAttachments from '../components/EmailAttachments';
import BuyerAgreementEmails from '../components/BuyerAgreementEmails';
import BuyerAgreementTeam from '../components/BuyerAgreementTeam';
import ComplianceIssueBar from '../components/ComplianceIssueBar';
import useViewerSubscription from '../hooks/useViewerSubscription';
import './ContractDetail.css';
import './BuyerAgreementDetail.css';
import '../components/ContractInfo.css';
import { getBackendBaseUrl, getContractApiUrl } from '../utils/apiUrls';
import { portalLoginAuthorizationParams } from '../utils/authSession';
import ChatBox from '../components/ChatBox';

const BASE_URL = getBackendBaseUrl();


const EVENT_TYPE_LABELS = {
  meeting: 'Meeting',
  home_tour: 'Home Tour',
  offer_date: 'Offer Date',
};

const BuyerAgreementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, loginWithRedirect, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const { canResolveCompliance } = useViewerSubscription();

  const [agreement, setAgreement] = useState(null);
  const [complianceIssues, setComplianceIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sectionStates, setSectionStates] = useState({
    details: true,
    parties: true,
    documents: false,
    emails: false,
    events: true,
  });

  // Event form state
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventForm, setEventForm] = useState({ title: '', event_type: 'meeting', event_date: '', event_time: '', location: '', notes: '' });
  const [savingEvent, setSavingEvent] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);

  const toggleSection = (key) =>
    setSectionStates(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (isAuthenticated && id) fetchAgreement();
  }, [isAuthenticated, id]);

  const fetchAgreement = async () => {
    try {
      setLoading(true);
      setError(null);
      const [res, complianceRes] = await Promise.all([
        axios.get(`${BASE_URL}/buyer-agreement/${id}`, {
          params: { user_email: user?.email },
        }),
        axios.get(`${getContractApiUrl()}/compliance-issues`, {
          params: { buyer_agreement_id: id, limit: 200, user_email: user?.email },
        }).catch(() => ({ data: { compliance_issues: [] } })),
      ]);
      setAgreement(res.data);
      setComplianceIssues(
        (complianceRes.data.compliance_issues || []).filter((issue) => issue.status === 'open')
      );
    } catch (err) {
      if (err.response?.status === 404) setError('Buyer agreement not found.');
      else setError('Failed to load buyer agreement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title.trim()) { alert('Title is required'); return; }
    try {
      setSavingEvent(true);
      if (editingEventId) {
        await axios.put(`${BASE_URL}/buyer-agreement/${id}/events/${editingEventId}`, eventForm, {
          params: { user_email: user?.email },
        });
      } else {
        await axios.post(`${BASE_URL}/buyer-agreement/${id}/events`, eventForm, {
          params: { user_email: user?.email },
        });
      }
      setShowEventForm(false);
      setEditingEventId(null);
      setEventForm({ title: '', event_type: 'meeting', event_date: '', event_time: '', location: '', notes: '' });
      fetchAgreement();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save event');
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteEvent = async (eid) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${BASE_URL}/buyer-agreement/${id}/events/${eid}`, {
        params: { user_email: user?.email },
      });
      fetchAgreement();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete event');
    }
  };

  const startEditEvent = (event) => {
    setEditingEventId(event.id);
    setEventForm({
      title: event.title || '',
      event_type: event.event_type || 'meeting',
      event_date: event.event_date || '',
      event_time: event.event_time || '',
      location: event.location || '',
      notes: event.notes || '',
    });
    setShowEventForm(true);
  };

  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <div className="auth-content">
          <h1>Access Denied</h1>
          <p>Please log in to view this buyer agreement.</p>
          <button
            className="login-button"
            onClick={() => loginWithRedirect({
              appState: { returnTo: `${location.pathname}${location.search}` },
              authorizationParams: portalLoginAuthorizationParams(),
            })}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <h2>Error</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={fetchAgreement} className="retry-button">Retry</button>
            <button onClick={() => navigate('/')} className="back-button">← Back</button>
          </div>
        </div>
      </div>
    );
  }

  const buyers = (agreement?.parties || []).filter(p => p.role === 'buyer' || p.role === 'co_buyer');
  const buyersDisplay = buyers.map(p => p.name).filter(Boolean).join(' & ') || '—';
  const isCancelled = agreement?.status === 'cancelled';

  return (
    <div className="contract-detail-container">
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <Logo size={72} variant="arrow" />
            <div className="header-title-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h1 className="property-address-title">{buyersDisplay}</h1>
                <span className="detail-type-badge detail-type-badge--buyer-agreement">Buyer Agreement</span>
              </div>
              <div className="contract-parties-subtitle">
                {agreement?.expiration_date && (
                  <span>Expires {agreement.expiration_date}</span>
                )}
                {isCancelled && <span style={{ marginLeft: agreement?.expiration_date ? 8 : 0, color: '#9ca3af' }}>· Cancelled</span>}
              </div>
            </div>
          </div>
          <div className="header-right" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <GenerateFormControl
              parentType="buyer_agreement"
              parentId={id}
              userEmail={user?.email}
              representedSides={agreement?.represented_sides}
            />
            <button className="back-button" onClick={() => navigate('/')}>← Back</button>
          </div>
        </div>
      </header>

      <main className="main-content">
        <ChatBox
          key={id ? `buyer_agreement:${id}` : 'home'}
          userEmail={user?.email}
          userName={user?.name || user?.nickname}
          getAccessTokenSilently={getAccessTokenSilently}
          getIdTokenClaims={getIdTokenClaims}
          entityHint={id ? `buyer_agreement:${id}` : undefined}
        />
        {agreement && (
          <>
            <ComplianceIssueBar
              issues={complianceIssues}
              canResolve={canResolveCompliance}
              apiUrl={getContractApiUrl()}
              onResolved={(issue) => {
                setComplianceIssues((prev) => prev.filter((item) => item.id !== issue.id));
              }}
            />

            {/* 1. Key People */}
            <section className="team-section">
              <BuyerAgreementTeam
                agreementId={agreement.id}
                parties={agreement.parties}
                userEmail={user?.email}
                isExpanded={sectionStates.parties}
                onToggle={() => toggleSection('parties')}
              />
            </section>

            {/* 2. Documents */}
            <section className="attachments-section">
              <EmailAttachments
                contractId={null}
                buyerAgreementId={agreement.id}
                propertyAddress={null}
                canSendInkless={agreement.can_inkless_send === true}
                canRecoverInkless={
                  agreement.can_read_parent === true && agreement.can_mutate === true
                }
                title="Buyer Documents"
                isExpanded={sectionStates.documents}
                onToggle={() => toggleSection('documents')}
              />
            </section>

            {/* 3. Email History */}
            <BuyerAgreementEmails
              buyerAgreementId={agreement.id}
              userEmail={user?.email}
              isExpanded={sectionStates.emails}
              onToggle={() => toggleSection('emails')}
            />

            {/* 5. Events */}
            <section>
              <SectionHeader
                title="Events"
                isExpanded={sectionStates.events}
                onToggle={() => toggleSection('events')}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {agreement.cal_uuid && (
                    <a
                      href={agreement.google_cal_id
                        ? `${BASE_URL}/buyer-agreement/public/google-calendar/${agreement.cal_uuid}`
                        : `${BASE_URL}/buyer-agreement/public/calendar/${agreement.cal_uuid}.ics`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      style={{
                        background: 'none',
                        border: '1px solid #D4A574',
                        borderRadius: '4px',
                        padding: '5px 12px',
                        cursor: 'pointer',
                        color: '#D4A574',
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {agreement.google_cal_id ? '📅 Calendar' : '📅 Subscribe'}
                    </a>
                  )}
                  <button
                    className="ba-add-event-btn"
                    onClick={(e) => { e.stopPropagation(); setEditingEventId(null); setEventForm({ title: '', event_type: 'meeting', event_date: '', event_time: '', location: '', notes: '' }); setShowEventForm(true); }}
                  >
                    + Add Event
                  </button>
                </div>
              </SectionHeader>
              {sectionStates.events && (
                <div style={{ padding: '8px 0' }}>
                  {showEventForm && (
                    <div className="ba-event-form">
                      <div className="ba-event-form-row">
                        <input
                          placeholder="Title *"
                          value={eventForm.title}
                          onChange={e => setEventForm(p => ({ ...p, title: e.target.value }))}
                          className="ba-event-input"
                        />
                        <select
                          value={eventForm.event_type}
                          onChange={e => setEventForm(p => ({ ...p, event_type: e.target.value }))}
                          className="ba-event-input"
                        >
                          <option value="meeting">Meeting</option>
                          <option value="home_tour">Home Tour</option>
                          <option value="offer_date">Offer Date</option>
                        </select>
                        <input
                          type="date"
                          value={eventForm.event_date}
                          onChange={e => setEventForm(p => ({ ...p, event_date: e.target.value }))}
                          className="ba-event-input"
                        />
                        <input
                          type="time"
                          value={eventForm.event_time}
                          onChange={e => setEventForm(p => ({ ...p, event_time: e.target.value }))}
                          className="ba-event-input"
                        />
                      </div>
                      <div className="ba-event-form-row">
                        <input
                          placeholder="Location"
                          value={eventForm.location}
                          onChange={e => setEventForm(p => ({ ...p, location: e.target.value }))}
                          className="ba-event-input"
                          style={{ flex: 2 }}
                        />
                        <input
                          placeholder="Notes"
                          value={eventForm.notes}
                          onChange={e => setEventForm(p => ({ ...p, notes: e.target.value }))}
                          className="ba-event-input"
                          style={{ flex: 3 }}
                        />
                      </div>
                      <div className="ba-event-form-actions">
                        <button className="ba-save-btn" onClick={handleSaveEvent} disabled={savingEvent}>
                          {savingEvent ? 'Saving…' : (editingEventId ? 'Update Event' : 'Add Event')}
                        </button>
                        <button className="ba-cancel-btn" onClick={() => { setShowEventForm(false); setEditingEventId(null); }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {(agreement.events || []).length === 0 && !showEventForm && (
                    <p style={{ padding: '12px 16px', color: '#9ca3af', fontSize: '0.9rem' }}>No events yet.</p>
                  )}

                  {(agreement.events || []).map((evt) => (
                    <div key={evt.id} className="ba-event-row">
                      <div className="ba-event-date">{evt.event_date || '—'}</div>
                      <div className="ba-event-info">
                        <span className="ba-event-title">{evt.title}</span>
                        <span className="ba-event-type">{EVENT_TYPE_LABELS[evt.event_type] || evt.event_type || ''}</span>
                        {evt.location && <span className="ba-event-location">📍 {evt.location}</span>}
                        {evt.notes && <span className="ba-event-notes">{evt.notes}</span>}
                      </div>
                      <div className="ba-event-actions">
                        <button onClick={() => startEditEvent(evt)} className="ba-edit-btn">Edit</button>
                        <button onClick={() => handleDeleteEvent(evt.id)} className="ba-del-btn">×</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* 5. Agreement Details */}
            <section className="contract-info-section">
              <SectionHeader
                title="Agreement Details"
                isExpanded={sectionStates.details}
                onToggle={() => toggleSection('details')}
              />
              {sectionStates.details && (
                <div className="contract-info-container">
                  <div className="ba-details-grid">
                    {[
                      ['Agreement Date', agreement.agreement_date],
                      ["Buyer's Agent Commission", agreement.buyers_agent_commission_percent
                        ? `${(parseFloat(agreement.buyers_agent_commission_percent) * 100).toFixed(2)}%`
                        : agreement.buyers_agent_commission_amount
                          ? `$${parseFloat(agreement.buyers_agent_commission_amount).toLocaleString()}`
                          : null],
                      ['Commission Payer', agreement.buyers_agent_commission_payer],
                      ['Retainer Fee', agreement.retainer_fee_amount ? `$${parseFloat(agreement.retainer_fee_amount).toLocaleString()}` : null],
                      ['TC Fee', agreement.buyers_tc_fee_amount ? `$${parseFloat(agreement.buyers_tc_fee_amount).toLocaleString()}` : null],
                    ].filter(([, v]) => v != null).map(([label, value]) => (
                      <div key={label} className="ba-detail-row">
                        <span className="ba-detail-label">{label}</span>
                        <span className="ba-detail-value">{value}</span>
                      </div>
                    ))}
                  </div>
                  <ReferralInfoSection referralDetails={agreement.referral_details} />
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default BuyerAgreementDetail;
